import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import ModalDragStrip from "../ModalDragStrip";
import { formatApiRequestError } from "../../api/openapi_clients";

type RunDialogShellProps = {
  /** Id of the heading inside, which names the dialog to assistive tech. */
  labelledBy: string;
  /** What the backdrop button announces itself as — how this dialog is left. */
  dismissLabel: string;
  onDismiss: () => void;
  children: React.ReactNode;
};

/**
 * The backdrop, window drag strip and card the analysis-run dialogs sit in.
 * See `ModalDragStrip` for why the strip is a layer of its own.
 *
 * Rendered into `document.body`: these dialogs are opened from inside the
 * analytics header, whose ancestors form stacking contexts of their own, and a
 * `z-[110]` measured against one of those still loses to the menus and popovers
 * that portal themselves to the body.
 */
export const RunDialogShell = ({
  labelledBy,
  dismissLabel,
  onDismiss,
  children,
}: RunDialogShellProps) =>
  createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <button
        type="button"
        aria-label={dismissLabel}
        className="absolute inset-0 cursor-default"
        onClick={onDismiss}
      />
      <ModalDragStrip />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-outline bg-surface p-5 text-text shadow-2xl">
        {children}
      </div>
    </div>,
    document.body,
  );

export const cancelButtonClass =
  "rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text transition hover:bg-muted-surface disabled:opacity-60";
export const dangerButtonClass =
  "rounded-lg border border-danger bg-danger px-3 py-2 text-sm font-medium text-white transition hover:bg-danger-strong disabled:cursor-not-allowed disabled:opacity-60";
export const primaryButtonClass =
  "rounded-lg border border-info bg-info px-3 py-2 text-sm font-medium text-white transition hover:bg-info-strong disabled:cursor-not-allowed disabled:opacity-60";

type RunDialogActionsProps = {
  busy: boolean;
  confirmLabel: string;
  confirmClassName: string;
  onCancel: () => void;
  onConfirm: () => void;
};

/** Cancel beside one confirming action: the footer of a run dialog. */
export const RunDialogActions = ({
  busy,
  confirmLabel,
  confirmClassName,
  onCancel,
  onConfirm,
}: RunDialogActionsProps) => {
  const { t } = useTranslation();
  return (
    <div className="mt-5 flex justify-end gap-2">
      <button type="button" disabled={busy} onClick={onCancel} className={cancelButtonClass}>
        {t("cancel")}
      </button>
      <button type="button" disabled={busy} onClick={onConfirm} className={confirmClassName}>
        {busy ? t("loading_ellipsis") : confirmLabel}
      </button>
    </div>
  );
};

/**
 * Run one dialog's action, holding the dialog busy while it is in flight and
 * keeping its failure on screen instead of closing over it.
 */
export const useRunAction = (fallbackMessage: string) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      // Through the API formatter, so a server that said something specific —
      // "this recording is already being analyzed" — reaches the dialog that
      // asked, instead of the client's generic "could not" line.
      setError(err instanceof Error ? formatApiRequestError(err, t) : fallbackMessage);
    } finally {
      setBusy(false);
    }
  };

  return { busy, error, run };
};
